import React, { useState } from 'react';
import { Button, Typography, Row, Col, Card, Space, Divider, Modal, Form, Input, Checkbox, message } from 'antd';
import { 
  BarChartOutlined, 
  GlobalOutlined, 
  CheckCircleOutlined,
  ArrowRightOutlined,
  LineChartOutlined,
  PieChartOutlined,
  MailOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

const { Title, Text, Paragraph } = Typography;

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [pageLoadTime] = useState(Date.now());
  
  const [sessionId] = useState(() => {
    const existing = localStorage.getItem('waitlist_session_id');
    if (existing) return existing;
    
    const generateId = () => {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
      });
    };
    
    const newId = generateId();
    localStorage.setItem('waitlist_session_id', newId);
    return newId;
  });

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleSubmit = async (values: any) => {
    if (isSubmitting) return;
    
    message.success('You\'re on the list. We\'ll reach out when your access is approved.');
    setIsSubmitted(true);
    form.resetFields();
    setIsSubmitting(true);
    
    try {
      const hiddenFields = {
        referrer_url: document.referrer || undefined,
        landing_path: window.location.pathname,
        session_id: sessionId,
        honeypot: '',
        submit_timing: Date.now() - pageLoadTime
      };

      const response = await fetch('https://pharmadashv2-backend.onrender.com/api/waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: values.email,
          marketing_opt_in: values.marketing_opt_in,
          ...hiddenFields
        }),
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      const result = await response.json();
      
      if (result.status === 'accepted' || result.status === 'ok') {
        // Success - already shown message
      } else {
        throw new Error(result.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Waitlist submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateCompanyEmail = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error('Please enter your email'));
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return Promise.reject(new Error('Please enter a valid email'));
    }
    
    // List of free email providers to reject
    const freeEmailProviders = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
      'aol.com', 'icloud.com', 'mail.com', 'protonmail.com', 'yandex.com',
      'gmx.com', 'zoho.com', 'msn.com', 'inbox.com', 'mail.ru'
    ];
    
    const emailDomain = value.toLowerCase().split('@')[1];
    
    if (freeEmailProviders.includes(emailDomain)) {
      return Promise.reject(new Error('Please use your company email address'));
    }
    
    return Promise.resolve();
  };

  const features = [
    {
      icon: <BarChartOutlined />,
      title: 'Market Intelligence',
      description: 'Interactive pharmaceutical market data and analytics'
    },
    {
      icon: <LineChartOutlined />,
      title: 'Pipeline Analysis',
      description: 'Comprehensive drug pipeline tracking and insights'
    },
    {
      icon: <GlobalOutlined />,
      title: 'Regional Insights',
      description: 'Geographic market analysis and regional trends'
    },
    {
      icon: <PieChartOutlined />,
      title: 'Sub-Market Breakdowns',
      description: 'Granular breakdowns by sub-segments'
    }
  ];

  const coverage = [
    { label: 'Global Markets', value: '26' },
    { label: 'Companies', value: '300+' },
    { label: 'Pipeline Drugs', value: '1000+' },
    { label: 'Forecast Horizon', value: '2026-2036' }
  ];

  const pricingTiers = [
    {
      name: 'Focus',
      planId: 'focus',
      price: '$150',
      period: '/month',
      features: ['1 therapeutic area', 'Email support'],
      popular: false
    },
    {
      name: 'In Sight',
      planId: 'in-sight',
      price: '$300',
      period: '/month',
      features: ['Multiple Focus areas', 'Cross-area benchmarking', 'Priority support'],
      popular: true
    },
    {
      name: 'Panorama',
      planId: 'panorama',
      price: '$500',
      period: '/month',
      features: ['All therapeutic areas', 'Cross-area benchmarking', 'Custom analytics', 'Dedicated support'],
      popular: false
    }
  ];



  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-navbar">
        <div className="nav-container">
          <div className="nav-left">
            <div 
              className="nav-logo"
              role="button"
              tabIndex={0}
              onClick={() => navigate('/')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  navigate('/');
                }
              }}
            >
              <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
            </div>
            <div className="nav-links">
              <a href="/platform">Platform</a>
              <a href="/markets">Markets</a>
              <a href="/pulse">Pulse</a>
              <a href="/press">Press</a>
            </div>
          </div>
          <div className="nav-actions">
            <Button type="primary" onClick={() => navigate('/login')}>Open Portal</Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="section-container">
          <Row gutter={[48, 32]} align="middle">
            <Col xs={24} lg={12}>
              <div className="hero-content">
                <Title level={1} className="hero-title">
                  Introducing SNS Market Intelligence Portal
                </Title>
                <Paragraph className="hero-description">
                  Access comprehensive pharmaceutical market data, pipeline analysis, and regional insights. 
                  Make data-driven decisions with real-time intelligence from the world's leading pharma markets.
                </Paragraph>
                <Space size="large" className="hero-buttons">
                  <Button 
                    type="primary" 
                    size="large" 
                    onClick={showModal}
                  >
                    Request Free Trial
                  </Button>
                  <Button 
                    size="large" 
                    onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    View Pricing
                  </Button>
                </Space>
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <div className="hero-visual">
                <Card className="hero-card" bordered={false}>
                  <div className="portal-screenshot">
                    <img 
                      src={process.env.PUBLIC_URL + '/portal-screenshot.png'} 
                      alt="SNS Intelligence Portal Dashboard" 
                      style={{ 
                        width: '100%', 
                        height: 'auto', 
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                      }}
                    />
                  </div>
                </Card>
              </div>
            </Col>
          </Row>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="section-container">
          <div className="section-header">
            <Title level={2} className="section-title">Powerful Features</Title>
            <Paragraph className="section-subtitle">
              Everything you need to understand and navigate the pharmaceutical landscape
            </Paragraph>
          </div>
          <Row gutter={[24, 24]}>
            {features.map((feature, index) => (
              <Col xs={24} sm={12} lg={6} key={index}>
                <Card className="feature-card" bordered={false}>
                  <div className="feature-icon">{feature.icon}</div>
                  <Title level={4} className="feature-title">{feature.title}</Title>
                  <Text className="feature-description">{feature.description}</Text>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Coverage Section */}
      <section className="coverage-section">
        <div className="section-container">
          <div className="section-header">
            <Title level={2} className="section-title">Global Coverage</Title>
            <Paragraph className="section-subtitle">
              Comprehensive data coverage across the pharmaceutical ecosystem
            </Paragraph>
          </div>
          <Row gutter={[24, 24]}>
            {coverage.map((item, index) => (
              <Col xs={12} sm={6} key={index}>
                <Card className="coverage-card" bordered={false}>
                  <div className="coverage-number">{item.value}</div>
                  <div className="coverage-label">{item.label}</div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="section-container">
          <div className="section-header">
            <Title level={2} className="section-title">Pricing</Title>
            <Paragraph className="section-subtitle">
              Choose the plan that fits your organization's needs
            </Paragraph>
          </div>
          <Row gutter={[24, 24]} justify="center">
            {pricingTiers.map((tier, index) => (
              <Col xs={24} sm={12} lg={8} key={index}>
                <Card 
                  className={`pricing-card ${tier.popular ? 'popular' : ''} ${tier.planId === 'panorama' ? 'disabled' : ''}`} 
                  bordered={false}
                  onClick={tier.planId === 'panorama' ? undefined : () => navigate(`/subscribe?plan=${tier.planId}`)}
                  style={tier.planId === 'panorama' ? {
                    backgroundColor: '#f5f5f5',
                    opacity: 0.6,
                    position: 'relative'
                  } : {}}
                >
                  
                  <div className="pricing-header">
                    <Title level={3} className="pricing-name" style={tier.planId === 'panorama' ? { color: '#8c8c8c' } : {}}>{tier.name}</Title>
                                         <div className="pricing-price">
                       <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '8px' }}>Starting from</div>
                       <span className="price-amount" style={tier.planId === 'panorama' ? { color: '#8c8c8c' } : {}}>{tier.price}</span>
                       <span className="price-period" style={tier.planId === 'panorama' ? { color: '#8c8c8c' } : {}}>{tier.period}</span>
                     </div>
                  </div>
                  <ul className="pricing-features">
                    {tier.features.map((feature, featureIndex) => (
                      <li key={featureIndex}>
                        <CheckCircleOutlined className="feature-check" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                                    <Button 
                    type={tier.planId === 'panorama' ? 'default' : 'default'} 
                    block 
                    size="large"
                    disabled={tier.planId === 'panorama'}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (tier.planId !== 'panorama') {
                        navigate(`/subscribe?plan=${tier.planId}`);
                      }
                    }}
                    style={tier.planId === 'panorama' ? {
                      backgroundColor: '#f0f0f0',
                      color: '#8c8c8c',
                      cursor: 'not-allowed'
                    } : {}}
                  >
                    {tier.planId === 'panorama' ? 'Coming Soon' : 'Get Started'}
                  </Button>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      

      {/* CTA Section */}
      <section className="cta-section">
        <div className="section-container">
          <Card className="cta-card" bordered={false}>
            <div className="cta-content">
              <Title level={2} className="cta-title">Ready to Get Started?</Title>
              <Paragraph className="cta-description">
                Join the growing number of pharma professionals who trust SNS Intelligence to stay ahead of competition.
              </Paragraph>
              <Space size="large" className="cta-buttons">
                <Button 
                  type="primary" 
                  size="large" 
                  onClick={showModal}
                >
                  Request Free Trial
                </Button>
                <Button 
                  size="large" 
                  onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  View Pricing
                </Button>
              </Space>
              <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid rgba(255, 255, 255, 0.3)' }}>
                <Text style={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: '18px', fontWeight: 500 }}>
                  Have questions? Contact us at{' '}
                  <a 
                    href="mailto:info@snsintel.com" 
                    style={{ 
                      color: '#fff', 
                      textDecoration: 'none',
                      borderBottom: '2px solid rgba(255, 255, 255, 0.5)',
                      paddingBottom: '2px',
                      fontWeight: 600
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderBottomColor = 'rgba(255, 255, 255, 1)';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderBottomColor = 'rgba(255, 255, 255, 0.5)';
                      e.currentTarget.style.color = '#fff';
                    }}
                  >
                    info@snsintel.com
                  </a>
                </Text>
              </div>
            </div>
          </Card>
        </div>
      </section>

            {/* Footer */}
      <footer className="landing-footer">
        <div className="section-container">
          <div className="footer-content">
            <div className="footer-logo-section">
              <div className="footer-logo">
                <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
              </div>
            </div>
            <div className="footer-links-section">
              <div className="footer-column">
                <Title level={5}>Product</Title>
                <div className="footer-links">
                  <a href="/platform">Platform</a>
                  <a href="/data-coverage">Data Coverage</a>
                  <a href="/markets">Markets</a>
                  <a href="/pulse">Pulse</a>
                </div>
              </div>
              <div className="footer-column">
                <Title level={5}>Company</Title>
                <div className="footer-links">
                  <a href="/about">About</a>
                  <a href="/contact">Contact</a>
                  <a href="/press">Press</a>
                </div>
              </div>
            </div>
          </div>
          <Divider />
          <div className="footer-bottom">
            <Text>&copy; 2026 SNS Intelligence. All rights reserved.</Text>
            <div className="footer-legal">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Trial Request Modal */}
      <Modal
        title="Request Free Trial"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={500}
      >
        {!isSubmitted ? (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
          >
            <Form.Item
              name="email"
              label="Company Email"
              rules={[{ validator: validateCompanyEmail }]}
            >
              <Input 
                placeholder="your.email@company.com" 
                size="large"
                prefix={<MailOutlined />}
              />
            </Form.Item>

            <Form.Item
              name="marketing_opt_in"
              valuePropName="checked"
              rules={[{ 
                validator: (_, value) => 
                  value ? Promise.resolve() : Promise.reject(new Error('Please accept to continue'))
              }]}
            >
              <Checkbox>
                I agree to receive updates about the trial and marketing communications.
              </Checkbox>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                size="large"
                loading={isSubmitting}
                block
                htmlType="submit"
              >
                Request Free Trial
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
            <Title level={4}>You're on the list!</Title>
            <Paragraph>
              We'll reach out when your access is approved.
            </Paragraph>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LandingPage;
